import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Settings, FileText, Layers } from 'lucide-react'
import { toast } from 'sonner'
import * as configV2Api from '@/api/configurationV2'
import { getTemplates } from '@/api/configuration'
import type { LearningConfigurationV2 } from '@/types/configurationV2'
import { createDefaultLearningConfigurationV2 } from '@/types/configurationV2'
import ConfigurationBuilderV2 from './ConfigurationBuilderV2'

interface ProductModeSelectorProps {
  productId: number
  currentMode?: string | null
  currentTemplateId?: number | null
}

type ConfigMode = 'default' | 'single_template' | 'per_hierarchy'

export default function ProductModeSelector({
  productId,
  currentMode,
  currentTemplateId,
}: ProductModeSelectorProps) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState<ConfigMode>((currentMode as ConfigMode) || 'default')
  const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(currentTemplateId ?? null)
  const [showBuilder, setShowBuilder] = useState(false)
  const [v2Config, setV2Config] = useState<LearningConfigurationV2 | null>(null)

  const { data: templates = [] } = useQuery({
    queryKey: ['configuration-templates'],
    queryFn: getTemplates,
  })

  const { data: existingV2Config } = useQuery({
    queryKey: ['configuration-v2', 'product', productId],
    queryFn: () => configV2Api.getProductConfigurationV2(productId),
  })

  const modeMutation = useMutation({
    mutationFn: () => configV2Api.setProductConfigMode(productId, mode, selectedTemplateId ?? undefined),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuration-v2', 'product', productId] })
      toast.success('Configuration 모드가 저장되었습니다')
    },
    onError: () => toast.error('저장에 실패했습니다'),
  })

  const saveV2Mutation = useMutation({
    mutationFn: (config: LearningConfigurationV2) =>
      configV2Api.setProductConfigurationV2(productId, config),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['configuration-v2', 'product', productId] })
      setShowBuilder(false)
      toast.success('v2 Configuration이 저장되었습니다')
    },
    onError: () => toast.error('저장에 실패했습니다'),
  })

  const templateList = Array.isArray(templates) ? templates : (templates as any)?.templates ?? []

  const handleModeChange = (newMode: ConfigMode) => {
    setMode(newMode)
    if (newMode !== 'single_template') {
      setSelectedTemplateId(null)
    }
  }

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-purple-700 mb-2">v2 Configuration 모드</div>

      {/* Mode Options */}
      <div className="space-y-2">
        <label
          className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
            mode === 'default' ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
          }`}
        >
          <input
            type="radio"
            name={`config-mode-${productId}`}
            checked={mode === 'default'}
            onChange={() => handleModeChange('default')}
            className="mt-0.5"
          />
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <Settings className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-sm font-medium">Content Default 사용</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">Content-Configuration 매핑에 설정된 기본값 사용</p>
          </div>
        </label>

        <label
          className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
            mode === 'single_template' ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
          }`}
        >
          <input
            type="radio"
            name={`config-mode-${productId}`}
            checked={mode === 'single_template'}
            onChange={() => handleModeChange('single_template')}
            className="mt-0.5"
          />
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <FileText className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-sm font-medium">단일 템플릿 적용</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">Product 내 모든 content에 하나의 템플릿 적용</p>
            {mode === 'single_template' && (
              <>
                <select
                  value={selectedTemplateId ?? ''}
                  onChange={(e) => setSelectedTemplateId(e.target.value ? parseInt(e.target.value) : null)}
                  className="mt-2 w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                >
                  <option value="">템플릿 선택...</option>
                  {templateList.map((t: any) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
                {selectedTemplateId && (
                  <button
                    onClick={(e) => {
                      e.preventDefault()
                      const selectedTemplate = templateList.find((t: any) => t.id === selectedTemplateId)
                      if (selectedTemplate) {
                        const templateConfig = selectedTemplate.configuration
                        const isV2 = (templateConfig as any)?.version === '2.0'
                        const configToUse = isV2
                          ? (templateConfig as unknown as LearningConfigurationV2)
                          : createDefaultLearningConfigurationV2()
                        setMode('per_hierarchy')
                        setV2Config(configToUse)
                        setShowBuilder(true)
                      }
                    }}
                    className="mt-2 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100"
                  >
                    이 템플릿 기반으로 커스터마이즈
                  </button>
                )}
              </>
            )}
          </div>
        </label>

        <label
          className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
            mode === 'per_hierarchy' ? 'border-purple-400 bg-purple-50' : 'border-gray-200 hover:bg-gray-50'
          }`}
        >
          <input
            type="radio"
            name={`config-mode-${productId}`}
            checked={mode === 'per_hierarchy'}
            onChange={() => handleModeChange('per_hierarchy')}
            className="mt-0.5"
          />
          <div className="flex-1">
            <div className="flex items-center gap-1.5">
              <Layers className="w-3.5 h-3.5 text-gray-500" />
              <span className="text-sm font-medium">Hierarchy별 별도 지정</span>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">5레벨 탭으로 세부 설정 가능</p>
            {mode === 'per_hierarchy' && (
              <button
                onClick={() => {
                  setV2Config(existingV2Config ?? createDefaultLearningConfigurationV2())
                  setShowBuilder(true)
                }}
                className="mt-2 px-3 py-1.5 text-xs font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700"
              >
                {existingV2Config ? '설정 편집' : '설정 구성'}
              </button>
            )}
          </div>
        </label>
      </div>

      {/* Save Mode Button */}
      {mode !== 'per_hierarchy' && (
        <button
          onClick={() => modeMutation.mutate()}
          disabled={modeMutation.isPending || (mode === 'single_template' && !selectedTemplateId)}
          className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
        >
          모드 저장
        </button>
      )}

      {/* v2 Builder Modal */}
      {showBuilder && v2Config && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-6">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto p-6">
            <h3 className="text-lg font-bold mb-4">Learning Configuration v2 설정</h3>
            <ConfigurationBuilderV2
              initialConfiguration={v2Config}
              onChange={(config) => setV2Config(config)}
            />
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
              <button
                onClick={() => setShowBuilder(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                취소
              </button>
              <button
                onClick={() => v2Config && saveV2Mutation.mutate(v2Config)}
                disabled={saveV2Mutation.isPending}
                className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
